/**
 * TypeScript types and interfaces for the Prompt Library system
 * Follows the existing codebase patterns and supports Supabase integration
 */

// Nonprofit organization categories aligned with database schema
export enum NonprofitCategory {
  GRANT_WRITING = 'grant_writing',
  FUNDRAISING = 'fundraising',
  COMMUNICATIONS = 'communications',
  PROGRAM_MANAGEMENT = 'program_management',
  MARKETING = 'marketing',
  DATA_ANALYSIS = 'data_analysis',
  RESEARCH = 'research',
  PROTOTYPING = 'prototyping',
  BRAINSTORMING = 'brainstorming'
}

// Category icons for file tree display
export const CATEGORY_ICONS: Record<NonprofitCategory, string> = {
  [NonprofitCategory.GRANT_WRITING]: '📝',
  [NonprofitCategory.FUNDRAISING]: '💰',
  [NonprofitCategory.COMMUNICATIONS]: '📢',
  [NonprofitCategory.PROGRAM_MANAGEMENT]: '📋',
  [NonprofitCategory.MARKETING]: '📈',
  [NonprofitCategory.DATA_ANALYSIS]: '📊',
  [NonprofitCategory.RESEARCH]: '🔍',
  [NonprofitCategory.PROTOTYPING]: '🛠️',
  [NonprofitCategory.BRAINSTORMING]: '💡'
};

// Human-readable category labels
export const CATEGORY_LABELS: Record<NonprofitCategory, string> = {
  [NonprofitCategory.GRANT_WRITING]: 'Grant Writing',
  [NonprofitCategory.FUNDRAISING]: 'Fundraising',
  [NonprofitCategory.COMMUNICATIONS]: 'Communications',
  [NonprofitCategory.PROGRAM_MANAGEMENT]: 'Program Management',
  [NonprofitCategory.MARKETING]: 'Marketing',
  [NonprofitCategory.DATA_ANALYSIS]: 'Data Analysis',
  [NonprofitCategory.RESEARCH]: 'Research',
  [NonprofitCategory.PROTOTYPING]: 'Prototyping',
  [NonprofitCategory.BRAINSTORMING]: 'Brainstorming'
};

// Prompt difficulty levels aligned with database schema
export enum PromptDifficulty {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate', 
  ADVANCED = 'advanced',
  SENIOR = 'senior'
}

// Prompt types aligned with database categories
export enum PromptType {
  GRANT_WRITING = 'grant_writing',
  FUNDRAISING = 'fundraising',
  COMMUNICATIONS = 'communications',
  PROGRAM_MANAGEMENT = 'program_management',
  MARKETING = 'marketing',
  DATA_ANALYSIS = 'data_analysis',
  RESEARCH = 'research',
  PROTOTYPING = 'prototyping',
  BRAINSTORMING = 'brainstorming'
}

// Variable types for prompt customization
export interface PromptVariable {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea' | 'date';
  required: boolean;
  placeholder?: string;
  options?: string[]; // For select type
  defaultValue?: string | number;
  description?: string;
}

// Rating and usage statistics
export interface PromptStats {
  usageCount: number;
  averageRating: number;
  totalRatings: number;
  lastUsed?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// User rating for a prompt
export interface PromptRating {
  id: string;
  promptId: string;
  userId: string;
  rating: number; // 1-5 stars
  review?: string;
  createdAt: Date;
}

// Main prompt interface
export interface Prompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category: NonprofitCategory;
  type: PromptType;
  difficulty: PromptDifficulty;
  tags: string[];
  variables: PromptVariable[];
  stats: PromptStats;
  authorId: string;
  authorName: string;
  isPublic: boolean;
  isFeatured: boolean;
  isActive: boolean;
}

// Filter options for prompt search
export interface PromptFilters {
  categories?: NonprofitCategory[];
  types?: PromptType[];
  difficulties?: PromptDifficulty[];
  tags?: string[];
  minRating?: number;
  isFeatured?: boolean;
  authorId?: string;
}

// Sort options for prompt list
export enum PromptSortBy {
  CREATED_DATE = 'created_date',
  UPDATED_DATE = 'updated_date',
  TITLE = 'title',
  USAGE_COUNT = 'usage_count',
  AVERAGE_RATING = 'average_rating',
  RELEVANCE = 'relevance'
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc'
}

export interface PromptSortOptions {
  sortBy: PromptSortBy;
  order: SortOrder;
}

// Search and filter state
export interface PromptSearchState {
  query: string;
  filters: PromptFilters;
  sortOptions: PromptSortOptions;
  currentPage: number;
  itemsPerPage: number;
}

// API response types for Supabase integration
export interface PromptListResponse {
  prompts: Prompt[];
  totalCount: number;
  hasMore: boolean;
}

// Component props interfaces
export interface PromptCardProps {
  prompt: Prompt;
  onUse: (prompt: Prompt) => void;
  onRate: (promptId: string, rating: number) => void;
  onBookmark?: (promptId: string) => void;
  isBookmarked?: boolean;
  showActions?: boolean;
  className?: string;
}

export interface PromptSearchProps {
  onSearch: (searchState: PromptSearchState) => void;
  initialState?: Partial<PromptSearchState>;
  placeholder?: string;
  showAdvancedFilters?: boolean;
  className?: string;
}

export interface CategoryFilterProps {
  selectedCategories: NonprofitCategory[];
  onCategoryChange: (categories: NonprofitCategory[]) => void;
  showCounts?: boolean;
  categoryCounts?: Record<NonprofitCategory, number>;
  className?: string;
}

export interface PromptDetailProps {
  prompt: Prompt;
  isOpen: boolean;
  onClose: () => void;
  onUse: (prompt: Prompt, variables: Record<string, any>) => void;
  onRate: (promptId: string, rating: number, review?: string) => void;
  userRating?: PromptRating;
  className?: string;
}

export interface PromptLibraryProps {
  initialFilters?: PromptFilters;
  showSearch?: boolean;
  showCategoryFilter?: boolean;
  itemsPerPage?: number;
  onPromptUse?: (prompt: Prompt, variables: Record<string, any>) => void;
  className?: string;
}

// PromptIDE component props
export interface PromptIDEProps {
  className?: string;
}

export interface FileTreeExplorerProps {
  tree: FileTreeNode;
  selectedFileId?: string;
  onFileSelect: (fileId: string, promptId?: string) => void;
  onFolderToggle: (folderId: string) => void;
  className?: string;
}

export interface PromptViewerProps {
  prompt: Prompt | null;
  onCopy: (content: string) => void;
  onUse: (prompt: Prompt, variables?: Record<string, string>, processedContent?: string) => void;
  className?: string;
}

// Chat interfaces for PromptIDE
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface GPTMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatBuilderSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  created_at: Date;
  updated_at: Date;
  userId?: string;
  currentPrompt?: string;
  variables?: Record<string, any>;
}

// Utility types
export type PromptFormData = Omit<Prompt, 'id' | 'stats' | 'authorId' | 'authorName'>;
export type PromptUpdate = Partial<Pick<Prompt, 'title' | 'description' | 'content' | 'tags' | 'variables' | 'isPublic' | 'isFeatured' | 'isActive'>>;

// Error types
export interface PromptLibraryError {
  code: string;
  message: string;
  details?: any;
}

// Difficulty display labels
export const DIFFICULTY_LABELS: Record<PromptDifficulty, string> = {
  [PromptDifficulty.BEGINNER]: 'Beginner',
  [PromptDifficulty.INTERMEDIATE]: 'Intermediate',
  [PromptDifficulty.ADVANCED]: 'Advanced',
  [PromptDifficulty.SENIOR]: 'Senior Level'
};

// Prompt type display labels
export const PROMPT_TYPE_LABELS: Record<PromptType, string> = {
  [PromptType.GRANT_WRITING]: 'Grant Writing',
  [PromptType.FUNDRAISING]: 'Fundraising',
  [PromptType.COMMUNICATIONS]: 'Communications',
  [PromptType.PROGRAM_MANAGEMENT]: 'Program Management',
  [PromptType.MARKETING]: 'Marketing',
  [PromptType.DATA_ANALYSIS]: 'Data Analysis',
  [PromptType.RESEARCH]: 'Research',
  [PromptType.PROTOTYPING]: 'Prototyping',
  [PromptType.BRAINSTORMING]: 'Brainstorming'
};

// Default system prompt for Lyra chat builder
export const LYRA_SYSTEM_PROMPT = `You are Lyra, an AI assistant specializing in helping nonprofit organizations create effective prompts. Keep responses under 150 words and use plain text formatting.

FORMATTING RULES:
- Use bullet points (•) not markdown
- Use plain text, minimal bold/italics  
- No long code blocks or examples
- No repetitive suggestions
- Be direct and actionable

When helping build prompts:
1. Ask 1-2 specific questions first
2. Give 3-4 concrete suggestions max
3. Focus on their nonprofit context
4. Provide practical improvements only

Be concise, professional, and helpful.`;

// File tree node structure
export interface FileTreeNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  parentId?: string;
  children?: FileTreeNode[];
  isExpanded?: boolean;
  promptId?: string;
}

// File tree utility function
export const createFileTree = (prompts: Prompt[], autoExpandWithResults = false, expansionState: Record<string, boolean> = {}): FileTreeNode => {
  const folders: Record<string, FileTreeNode> = {};
  const rootFolder: FileTreeNode = {
    id: 'root',
    name: 'Prompt Library',
    type: 'folder',
    children: [],
    isExpanded: true
  };

  // Create folder structure - only create folders that have prompts
  const categoriesWithPrompts = new Set(prompts.map(prompt => prompt.category));
  
  Object.values(NonprofitCategory).forEach(category => {
    // Only create folders for categories that have prompts
    if (categoriesWithPrompts.has(category)) {
      const folderNode: FileTreeNode = {
        id: category,
        name: CATEGORY_LABELS[category],
        type: 'folder',
        parentId: 'root',
        children: [],
        isExpanded: expansionState[category] !== undefined ? expansionState[category] : autoExpandWithResults // Use state or auto-expand when filtering shows results
      };
      folders[category] = folderNode;
      rootFolder.children!.push(folderNode);
    }
  });

  // Add prompts as files, sorted alphabetically
  prompts
    .sort((a, b) => a.title.localeCompare(b.title))
    .forEach(prompt => {
      const fileNode: FileTreeNode = {
        id: `file-${prompt.id}`,
        name: prompt.title,
        type: 'file',
        parentId: prompt.category,
        promptId: prompt.id
      };
      
      const folder = folders[prompt.category];
      if (folder && folder.children) {
        folder.children.push(fileNode);
      }
    });

  // Sort folders by name for consistent display
  rootFolder.children?.sort((a, b) => a.name.localeCompare(b.name));

  return rootFolder;
};

// OpenAI API Integration Types
export interface OpenAIConfig {
  apiKey: string;
  model: string;
  maxTokens: number;
  temperature: number;
  stream: boolean;
}

export interface GPTStreamResponse {
  choices: Array<{
    delta: {
      content?: string;
      role?: string;
    };
    finish_reason?: string;
  }>;
}

// Enhanced Chat Builder Props with improved error handling
export interface ChatBuilderProps {
  session: ChatBuilderSession | null;
  onSendMessage?: (message: ChatMessage) => void;
  onSaveSession?: (session: ChatBuilderSession) => void;
  isLoading?: boolean;
  className?: string;
}

// Stream control interface for better abort handling
export interface StreamControlState {
  isStreaming: boolean;
  canAbort: boolean;
  controller: AbortController | null;
  retryCount: number;
  lastError: string | null;
}

// Enhanced error types for better error handling
export interface ChatError {
  type: 'network' | 'api' | 'abort' | 'rate_limit' | 'unknown';
  message: string;
  originalError?: Error;
  retryable: boolean;
}

// Chat session management types
export interface ChatSessionConfig {
  autoSave: boolean;
  maxRetries: number;
  retryDelay: number;
  streamTimeout: number;
}